import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useActivity } from '../../hooks/useActivity';

interface RecentActivityProps {
  showUserActivities?: boolean; // Si true, affiche les activités de l'utilisateur, sinon les activités globales
  maxActivities?: number;
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ 
  showUserActivities = false, 
  maxActivities = 10 
}) => {
  const { user } = useAuth();
  const { 
    activities, 
    loading, 
    error, 
    loadUserActivities, 
    loadGlobalActivities, 
    getRelativeTime, 
    formatActivityMessage 
  } = useActivity();

  useEffect(() => {
    const loadActivities = async () => {
      if (showUserActivities && user?.id) {
        await loadUserActivities(user.id, maxActivities);
      } else {
        await loadGlobalActivities(maxActivities);
      }
    };

    loadActivities();
    
    // Rafraîchir les activités toutes les 30 secondes
    const interval = setInterval(loadActivities, 30000);
    
    return () => clearInterval(interval);
  }, [user?.id, showUserActivities, maxActivities, loadUserActivities, loadGlobalActivities]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'like': return '❤️';
      case 'comment': return '💬';
      case 'follow': return '👤';
      case 'post': return '📝';
      case 'token_discovery': return '🪙';
      default: return '⚡';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'like': return 'text-red-400';
      case 'comment': return 'text-blue-400';
      case 'follow': return 'text-green-400';
      case 'post': return 'text-purple-400';
      case 'token_discovery': return 'text-yellow-400';
      default: return 'text-cyan-400';
    }
  };

  if (loading) {
    return (
      <div className="cosmic-card p-6">
        <h3 className="text-lg font-semibold text-emerald-100 mb-4 flex items-center">
          ⚡ <span className="ml-2 neon-text">Activité Récente</span>
        </h3>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-emerald-800/30 rounded mb-2"></div>
              <div className="h-3 bg-emerald-800/20 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cosmic-card p-6">
        <h3 className="text-lg font-semibold text-emerald-100 mb-4 flex items-center">
          ⚡ <span className="ml-2 neon-text">Activité Récente</span>
        </h3>
        <div className="text-red-400 text-sm text-center py-4">
          Erreur lors du chargement des activités
        </div>
      </div>
    );
  }

  return (
    <div className="cosmic-card p-6">
      <h3 className="text-lg font-semibold text-emerald-100 mb-4 flex items-center">
        ⚡ <span className="ml-2 neon-text">Activité Récente</span>
      </h3>
      
      {activities.length === 0 ? (
        <div className="text-emerald-300/60 text-sm text-center py-4">
          Aucune activité récente
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {activities.map((activity) => (
            <div 
              key={activity.id} 
              className="group hover:bg-emerald-900/20 rounded-lg p-2 -m-2 transition-colors"
            >
              <div className="flex items-start space-x-2">
                <span className="text-lg flex-shrink-0 mt-0.5">
                  {getActivityIcon(activity.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${getActivityColor(activity.type)} group-hover:text-emerald-100 transition-colors`}>
                    {formatActivityMessage(activity)}
                  </p>
                  <p className="text-emerald-300/60 text-xs mt-1">
                    {getRelativeTime(activity.createdAt)}
                  </p>
                  {activity.content && (
                    <p className="text-emerald-200/80 text-xs mt-1 truncate">
                      "{activity.content}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Indicateur de mise à jour automatique */}
      <div className="mt-4 pt-3 border-t border-emerald-800/30">
        <div className="flex items-center justify-center space-x-2 text-xs text-emerald-300/60">
          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
          <span>Mise à jour automatique</span>
        </div>
      </div>
    </div>
  );
};
